import { api } from "@breakout/backend/convex/_generated/api"
import { cn } from "@breakout/ui/utils"
import { convexQuery } from "@convex-dev/react-query"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import type { FunctionReturnType } from "convex/server"
import { Calendar, ChevronRight } from "lucide-react"
import { useState } from "react"
import { PageContainer } from "../components/layout/PageContainer"
import { Skeleton } from "../components/ui/Skeleton"
import { StatusBadge } from "../components/ui/StatusBadge"
import { useDocumentTitle } from "../hooks/common/useDocumentTitle"
import { useTabNavigation } from "../hooks/common/useTabNavigation"
import { formatShortDate } from "../lib/time"

export const Route = createFileRoute("/weeks")({
	component: WeeksPage,
})

// Infer type from backend query instead of manually defining
type WeekData = FunctionReturnType<typeof api.public.config.listAllWeeks>[number]

function WeeksPage() {
	// Context/store access
	const { navigateWithToast } = useTabNavigation()

	useDocumentTitle("Week Archive")

	// Data queries
	const { data: weeks, isLoading } = useQuery(convexQuery(api.public.config.listAllWeeks, {}))

	// Event handlers
	const handleWeekClick = (weekId: string) => {
		navigateWithToast({
			to: "/past/$weekId",
			params: { weekId },
		})
	}

	return (
		<div className="min-h-dvh bg-bg-primary text-text-primary">
			<PageContainer className="pt-5 pb-8 sm:pt-8">
				<div className="mb-5">
					<h1 className="font-bold text-lg text-text-primary">Week Archive</h1>
				</div>

				{isLoading ? (
					<div className="space-y-3">
						{Array.from({ length: 4 }, (_, i) => i).map(idx => (
							<div
								className="flex min-w-0 items-center gap-3 rounded-xl border border-border bg-bg-card px-4 py-3 shadow-sm lg:px-5 lg:py-4"
								key={idx}
							>
								<div className="min-w-0 flex-1">
									<div className="flex items-center gap-2">
										{/* Week date: font-semibold text-sm lg:text-base = h-5 lg:h-6 */}
										<Skeleton className="h-5 w-32 lg:h-6" />
										{/* Status badge placeholder */}
										<Skeleton className="h-5 w-16 rounded-full" />
									</div>
								</div>
								{/* Static chevron icon */}
								<ChevronRight aria-hidden="true" className="size-5 shrink-0 text-text-muted" />
							</div>
						))}
					</div>
				) : weeks && weeks.length > 0 ? (
					<div className="space-y-3">
						{weeks.map(week => (
							<WeekRow
								formatDate={formatShortDate}
								key={week.weekId}
								onClick={() => handleWeekClick(week.weekId)}
								week={week}
							/>
						))}
					</div>
				) : (
					<div className="rounded-2xl border border-border bg-bg-card p-10 text-center shadow-sm">
						<div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-bg-tertiary">
							<Calendar aria-hidden="true" className="size-8 text-text-muted" />
						</div>
						<h3 className="mb-2 text-balance font-display font-semibold text-lg text-text-primary">
							No Weeks Yet
						</h3>
						<p className="text-pretty text-base text-text-secondary">
							Weeks will appear here once they're created.
						</p>
					</div>
				)}
			</PageContainer>
		</div>
	)
}

function WeekRow({
	week,
	onClick,
	formatDate,
}: {
	week: WeekData
	onClick: () => void
	formatDate: (timestamp: number) => string
}) {
	const [isPressed, setIsPressed] = useState(false)
	const isActive = week.status === "active"
	const isClosed =
		week.status === "closed" || week.status === "resolved" || week.status === "finalized"

	const handleClick = () => {
		setIsPressed(true)
		setTimeout(() => {
			onClick()
		}, 120)
	}

	return (
		<button
			aria-label={`View week of ${formatDate(week.openTimeSec)}, ${isActive ? "active" : isClosed ? "closed" : "pending"}`}
			className={cn(
				"flex w-full min-w-0 items-center gap-3 rounded-xl border bg-bg-card px-4 py-3 text-left shadow-sm transition-[box-shadow,transform,background-color] duration-150 hover:shadow-lg lg:px-5 lg:py-4",
				isActive ? "border-green-500/30 bg-green-500/5" : "border-border",
				isPressed ? "scale-[0.98] bg-bg-card-hover" : "active:scale-[0.98] active:bg-bg-card-hover",
			)}
			onClick={handleClick}
			type="button"
		>
			{/* Week Info */}
			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-2">
					<span className="font-semibold text-sm text-text-primary lg:text-base">
						Week of {formatDate(week.openTimeSec)}
					</span>
					{isActive && <StatusBadge variant="active">Active</StatusBadge>}
					{isClosed && <StatusBadge variant="closed">Closed</StatusBadge>}
				</div>
			</div>

			{/* Chevron */}
			<ChevronRight aria-hidden="true" className="size-5 shrink-0 text-text-muted" />
		</button>
	)
}
